import {ChangeEvent, FocusEvent, useCallback, useRef, useState} from "react";
import {useSearchStore} from "@/Stores/Site/Search/SearchStore";
import {useModalStore} from "@/Stores/Site/ModalStore";
import {
    selectGetSearchData,
    selectOnBlur,
    selectOnChange,
    selectOnFocus,
    selectSetSearchQuery,
    selectSetSearchQueryOutput,
    selectSetSearchType,
} from "@/Stores/Site/Search/Selectors";
import {useSearchHistory} from "./useSearchHistory";
import {useSearchSuggestions} from "./useSearchSuggestions";
import {useSearchKeyboard} from "./useSearchKeyboard";
import {useSearchSubmit} from "./useSearchSubmit";
import {SearchSuggestion} from "@/entities/search";
import {router} from "@inertiajs/react";

export const useSearch = () => {
    const [opened, setOpened] = useState(false);

    const inputRef = useRef<HTMLInputElement | null>(null);

    /**
     * State
     */
    const closeSearchModal = useModalStore((state) => state.closeSearchModal);
    const searchQuery = useSearchStore((state) => state.searchQuery);
    const searchType = useSearchStore((state) => state.searchType);
    const page = useSearchStore((state) => state.page);
    const perPage = useSearchStore((state) => state.perPage);
    const filterParams = useSearchStore((state) => state.filterParams);
    const staticFilterParams = useSearchStore((state) => state.staticFilterParams);
    const sort = useSearchStore((state) => state.sort);
    const hint = useSearchStore((state) => state.hint);
    const resetParametersBeforeRequest = useSearchStore((state) => state.resetParametersBeforeRequest);

    /**
     * Actions
     */
    const getSearchData = useSearchStore(selectGetSearchData);
    const setSearchQuery = useSearchStore(selectSetSearchQuery);
    const setSearchType = useSearchStore(selectSetSearchType);
    const setSearchQueryOutput = useSearchStore(selectSetSearchQueryOutput);
    const searchOnChange = useSearchStore(selectOnChange);
    const searchOnFocus = useSearchStore(selectOnFocus);
    const searchOnBlur = useSearchStore(selectOnBlur);

    /**
     * History
     */
    const {history, addSearchTerm, clearHistory} = useSearchHistory();

    /**
     * Suggestions
     */
    const {suggestions, loading} = useSearchSuggestions({
        query: searchQuery,
        enabled: searchType === "suggestions",
        searchType,
        page,
        perPage,
        filterParams,
        staticFilterParams,
        sort,
    });

    /**
     * Submit
     */
    const {submitSearch} = useSearchSubmit({
        query: searchQuery,
        addSearchTerm,
        resetParametersBeforeRequest,
        setSearchType,
        setSearchQueryOutput,
        getSearchData,
    });

    /**
     * Keyboard navigation
     */
    const {
        handleKeyDown,
        cursorHistory,
        cursorSuggestions,
    } = useSearchKeyboard({
        suggestions,
        history,
        onSearch: submitSearch,
        onClose: closeSearchModal,
        onBlur: () => {
            searchOnBlur();
        },
        onHistorySelect: (value) => {
            setSearchQuery(value);
        },
        // onSuggestionSelect: (suggestion: SearchSuggestion) => {
        //     setSearchQuery(suggestion.h1);
        // },
        onSuggestionSubmit: (suggestion: SearchSuggestion) => {
            setSearchQuery(suggestion.h1);
            router.visit(suggestion.url);
        },
    });

    /**
     * Change
     */
    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        searchOnChange(e);
    }, [searchOnChange]);

    /**
     * Focus
     */
    const handleFocus = useCallback((e?: FocusEvent<HTMLInputElement>) => {
        setOpened(true);

        searchOnFocus(e);

        inputRef.current?.focus();
    }, [searchOnFocus]);

    /**
     * Blur
     */
    const handleBlur = useCallback((e?: FocusEvent<HTMLInputElement>) => {
        setTimeout(() => {
            setOpened(false);
        }, 150);

        setTimeout(() => {
            searchOnBlur(e);
        }, 500);
    }, [searchOnBlur]);

    /**
     * Clear query
     */
    const clearQuery = useCallback(() => {
        setSearchQuery("");

        inputRef.current?.focus();
    }, [setSearchQuery]);

    /**
     * History click
     */
    const handleHistoryClick = useCallback((value: string) => {
        closeSearchModal();

        setSearchQuery(value);

        submitSearch();
    }, [closeSearchModal, setSearchQuery, submitSearch]);

    return {
        opened,
        inputRef,
        query: searchQuery,
        hint,
        suggestions,
        loading,
        history,
        cursorHistory,
        cursorSuggestions,
        handleChange,
        handleFocus,
        handleBlur,
        handleKeyDown,
        handleHistoryClick,
        clearQuery,
        clearHistory,
        submitSearch,
    };
};
